import { ColDef, ColumnState, GridOptions } from 'ag-grid-community';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ApiResponse } from 'src/AngularJS/Models/api-response.model';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { GridHelperOptions } from 'src/shared/models/ag-grid-helper.models';
import { ModuleManager } from 'src/shared/models/module-manager';
import { ModuleManagerExportFormat } from 'src/shared/models/module-manager/module-manager-options.model';
import { ModuleManagerModuleSettings } from 'src/shared/models/module-manager/module-manager-settings.models';
import { ModuleManagerState } from 'src/shared/models/module-manager/module-manager-state.model';
import { ReportingSetData } from 'src/shared/models/pipeline-config/reporting-sets.models';
import { ReportingSetService } from './reporting-set.service';
import { AgGridAngular } from 'ag-grid-angular';

@Component({
    selector: 'cv-reporting-set',
    templateUrl: './reporting-set.component.html',
    styleUrls: ['./reporting-set.component.scss']
})
export class ReportingSetComponent implements OnInit {
    @ViewChild('reportingSetGrid') agGrid: AgGridAngular;

    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;

    gridOptions: GridOptions = null;
    loadingPromise: Promise<ApiResponse<ReportingSetData>> = null;
    isAborting = false;
    isFirstLoad = true;
    private columnDefs: ColDef[] = null;
    private _loadAborter: () => void;
    private _stateSaveTimer: any; // IPromise<void>
    private data: ReportingSetData;
    private prevData: ReportingSetData;

    // states
    private loadedColumnState: ColumnState[];
    private loadedPivotMode: boolean;
    private loadedFilterModel: any;

    constructor(
        private service: ReportingSetService,
        private agGridHelperService: AgGridHelperService,
    ) {
        // create a single bound callback function instead of a function for each event
        const queueStateSave = () => this.queueStateSave();

        this.gridOptions = this.agGridHelperService.createGridOptions({
            columnDefs: this.columnDefs,
            rowData: [],
            sideBar: true,
            onGridReady: (e) => {
                if (this.loadedColumnState == null)
                    e.api.autoSizeAllColumns();
            },
            onSortChanged: queueStateSave,
            onColumnPivotChanged: queueStateSave,
            onColumnPivotModeChanged: queueStateSave,
            onFilterChanged: queueStateSave,
            onColumnMoved: queueStateSave,
            onColumnPinned: queueStateSave,
            onColumnResized: queueStateSave,
            onColumnVisible: queueStateSave,
            onColumnRowGroupChanged: queueStateSave,
        }, {
            enableCopy: true
        } as GridHelperOptions);

        // use reporting set column types
        this.gridOptions.columnTypes = {...this.gridOptions.columnTypes, ...this.service.getAgGridColumnTypes()};
    }

    ngOnInit(): void {
        this.moduleManager.setOptions({
            hasRefresh: true,
            onSettingsApplied: this.onSettingsApplied.bind(this),
            onRefresh: this.onRefresh.bind(this),
            onExport: this.onExport.bind(this),
        });

        this.moduleManager.setMaxDateRange(14);

        const moduleColumnState: ModuleManagerState<ColumnState[]> = {
            name: 'columnState',
            autoLoad: true,
            getStateData: () => {
                if (!this.agGrid?.api)
                    throw Error("Not ready to save state."); // throw because we are not ready to save state

                return this.agGrid?.api.getColumnState();
            },
            onStateLoaded: stateData => {
                if (stateData == null)
                    return;

                this.loadedColumnState = stateData;

                if (this.agGrid?.api) {
                    if (this.columnDefs && this.service.upgradeColumnStates(this.columnDefs, this.loadedColumnState))
                        this.moduleManager.saveState('columnState');
                    this.agGrid?.api.applyColumnState({state: this.loadedColumnState});
                }
            }
        };

        const modulePivotMode: ModuleManagerState<boolean> = {
            name: 'pivotMode',
            autoLoad: true,
            getStateData: () => {
                if (!this.agGrid?.api)
                    throw Error("Not ready to save state."); // throw because we are not ready to save state

                return this.agGrid?.api.isPivotMode();
            },
            onStateLoaded: (stateData) => {
                if (stateData == null)
                    return;

                this.loadedPivotMode = Boolean(stateData);

                if (this.agGrid?.api && this.agGrid?.api.getAllDisplayedColumns().length) {
                    this.agGrid?.api.setGridOption('pivotMode',this.loadedPivotMode);
                }
            }
        };

        const moduleFilterModel: ModuleManagerState<any> = {
            name: 'filterModel',
            autoLoad: true,
            getStateData: () => {
                if (!this.agGrid?.api)
                    throw Error("Not ready to save state."); // throw because we are not ready to save state

                return this.agGrid?.api.getFilterModel();
            },
            onStateLoaded: (stateData) => {
                if (stateData == null)
                    return;

                this.loadedFilterModel = stateData;

                if (this.agGrid?.api) {
                    this.agGrid?.api.setFilterModel(stateData);
                }
            }
        };

        this.moduleManager.addState(moduleColumnState);
        this.moduleManager.addState(modulePivotMode);
        this.moduleManager.addState(moduleFilterModel);
    }

    async onSettingsApplied(settings: ModuleManagerModuleSettings) {
        const { reportingSetName } = this.moduleManager.moduleSettings;
        if (reportingSetName)
            this.moduleManager.setModuleNameSuffix(reportingSetName, true);
        this.loadData(false);
    }

    onExport(format: ModuleManagerExportFormat) {
        const { reportingSetName, dateField } = this.moduleManager.moduleSettings;
        return this.service.loadGenericReportingSetExport(reportingSetName, format, dateField, this.moduleKey);
    }

    onRefresh(isAutoRefresh: boolean) {
        this.loadData(isAutoRefresh);
    }

    async loadData(isAutoRefresh: boolean) {
        if (this.loadingPromise) { // cancelling data load
            this._loadAborter?.();
            this.isAborting = true;
            this.loadingPromise = null;
            this._loadAborter = null;
        }

        const { reportingSetName } = this.moduleManager.moduleSettings;
        let { dateField } = this.moduleManager.moduleSettings;
        const pageName = this.moduleKey;

        if (dateField === '[Current]') {
            // ignore dates when using current (DropAllOnImport only)
            dateField = null;
        }

        const abort = new Promise<void>((resolve) => this._loadAborter = () => resolve());
        this.loadingPromise = this.service.loadGenericReportingSetData(
            reportingSetName || '[load-from-state]',
            dateField,
            pageName,
            abort
        );
        try {
            this.moduleManager.showLoader = !isAutoRefresh;
            const response = await this.loadingPromise;
            if (response.status_code !== 200)
                throw response;
            this.prevData = this.data;
            this.data = response.data;
            this.moduleManager.setAutoRefreshReady();
        } catch (e) {
            if (this.isAborting) {
                this.moduleManager.handleLoadError(e, isAutoRefresh);
                this.isAborting = false;
                return;}
        } finally {
            this.loadingPromise = null;
            this._loadAborter = null;
        }

        this.moduleManager.showLoader = false;
        this.updateGrid();
        this.isFirstLoad = false;
        this.moduleManager.container.showDateRange = true;
    }

    updateGrid() {
        if (!this.data)
            return;

        this.columnDefs = this.data.FieldDefinitions.map(fd => {
            const coldDefs = {
                colId: fd.FieldName,
                enableRowGroup: true,
                enableValue: true,
                field: fd.FieldName,
                headerName: fd.FieldDisplay,
                type: this.service.getColumnType(fd.FieldType),
            } as ColDef;
            return coldDefs;
        });

        if (this.agGrid?.api) {
            let state: ColumnState[];
            let filterModel: any;

            if (!this.isFirstLoad) { // capture state before resetting columns
                state = this.agGrid?.api.getColumnState();
                filterModel = this.agGrid?.api.getFilterModel();
            }

            this.agGrid?.api.setGridOption('columnDefs', this.columnDefs);

            if (this.data.DataRows.length > 20000) // don't do a transaction if there are a lot of rows
                this.agGrid?.api.setGridOption('rowData', this.data.DataRows);
            else
                this.agGrid?.api.applyTransaction(
                    this.agGridHelperService.createTransaction(this.prevData?.DataRows, this.data.DataRows, this.gridOptions)
                );

            this.prevData = null;

            if (this.isFirstLoad) {
                if (this.loadedColumnState == null) { // auto size columns if no state is loaded
                    this.agGrid?.api.autoSizeAllColumns();
                } else {
                    // attempt to migrate old states, if they were migrated then re-save them
                    if (this.service.upgradeColumnStates(this.columnDefs, this.loadedColumnState)) {
                        this.moduleManager.saveState('columnState');
                    }

                    // set state to loaded state
                    this.agGrid?.api.applyColumnState({state: this.loadedColumnState});
                }

                if (this.loadedPivotMode != null)
                    this.agGrid?.api.setPivotMode(this.loadedPivotMode);
                if (this.loadedFilterModel != null) {
                    this.agGrid?.api.setFilterModel(this.loadedFilterModel);
                    this.agGrid?.api.onFilterChanged();
                }
            } else {
                this.agGrid?.api.applyColumnState({state});
                this.agGrid?.api.setFilterModel(filterModel);
                // sort and pivot keep their state here without restoring it manually
            }
        } else {
            this.gridOptions.rowData = this.data.DataRows;
            this.gridOptions.columnDefs = this.columnDefs;
        }
    }

    queueStateSave() {
        if (this._stateSaveTimer != null)
            clearTimeout(this._stateSaveTimer);

        this._stateSaveTimer = setTimeout(() => {
            this.moduleManager.saveState('columnState', 'sortModel', 'pivotMode', 'filterModel');
            this._stateSaveTimer = null;
        }, 500);
    }
}
